import { assertNever } from '@sparkdotfi/common-universal'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { createApproveActionConfig } from '../flavours/approve/logic/approveAction'
import { createApproveDelegationActionConfig } from '../flavours/approve-delegation/logic/approveDelegationAction'
import { createBorrowActionConfig } from '../flavours/borrow/logic/borrowAction'
import { createClaimAirdropRewardsActionConfig } from '../flavours/claim-airdrop-rewards/logic/claimSparkRewardsAction'
import { createClaimFarmRewardsActionConfig } from '../flavours/claim-farm-rewards/logic/claimFarmRewardsAction'
import { createClaimMarketRewardsActionConfig } from '../flavours/claim-market-rewards/logic/claimMarketRewardsAction'
import { createClaimPointsRewardsActionConfig } from '../flavours/claim-points-rewards/logic/claimPointsRewardsAction'
import { createClaimSparkRewardsActionConfig } from '../flavours/claim-spark-rewards/logic/claimSparkRewardsAction'
import { createDelegateActionConfig } from '../flavours/delegate/logic/delegateAction'
import { createDepositActionConfig } from '../flavours/deposit/logic/depositAction'
import { createDepositToSavingsActionConfig } from '../flavours/deposit-to-savings/logic/depositToSavingsAction'
import { createDepositToWethActionConfig } from '../flavours/deposit-to-weth/logic/depositToWethAction'
import { createFinalizeSpkUnstakeActionConfig } from '../flavours/finalize-spk-unstake/logic/finalizeSpkUnstakeAction'
import { createRepayActionConfig } from '../flavours/repay/logic/repayAction'
import { createResetApprovalActionConfig } from '../flavours/reset-approval/logic/resetApprovalAction'
import { createSetUseAsCollateralActionConfig } from '../flavours/set-use-as-collateral/logic/setUseAsCollateralAction'
import { createSetUserEModeActionConfig } from '../flavours/set-user-e-mode/logic/setUserEModeAction'
import { createStakeActionConfig } from '../flavours/stake/logic/stakeAction'
import { createStakeSpkActionConfig } from '../flavours/stake-spk/logic/stakeSpkAction'
import { createSwapActionConfig } from '../flavours/swap/logic/swapAction'
import { createUndelegateActionConfig } from '../flavours/undelegate/logic/undelegateAction'
import { createUnstakeActionConfig } from '../flavours/unstake/logic/unstakeAction'
import { createUnstakeSpkActionConfig } from '../flavours/unstake-spk/logic/unstakeSpkAction'
import { createUpgradeSdaiActionConfig } from '../flavours/upgrade-sdai/logic/upgradeSdaiAction'
import { createWithdrawActionConfig } from '../flavours/withdraw/logic/withdrawAction'
import { createWithdrawFromSavingsActionConfig } from '../flavours/withdraw-from-savings/logic/withdrawFromSavingsAction'
import { createWithdrawFromWethActionConfig } from '../flavours/withdraw-from-weth/logic/withdrawFromWethAction'
import { ActionConfig, ActionContext, InitialParamsQueryResult, VerifyTransactionResult } from './types'
import { useWalletType } from '@/domain/hooks/useWalletType'
import { runSingleActionPreWriteLifecycle } from '@/features/actions/logic/preWriteLifecycle'
import {
  UseRegularOrEip5792WriteResult,
  useRegularOrEip5792Write,
} from '@/features/actions/logic/transactions/useRegularOrEip5792Write'
import { Action, ActionHandler, ActionHandlerState, GetWriteConfigResult } from '@/features/actions/logic/types'
import { mapWriteResultToActionState } from '@/features/actions/logic/utils'

export const ACTION_QUERY_PREFIX = 'action-contract' as const
export const WRITE_QUERY_PREFIX = 'write' as const

function hashAction(action: Action): string {
  return JSON.stringify(action)
}

export interface UseContractActionParams {
  context: ActionContext
  action: Action
  actionId: string
  enabled: boolean
}
export function useContractAction({ action, actionId, context, enabled }: UseContractActionParams): ActionHandler {
  const config = actionToConfig(action, context)
  const { getInitialParams, getWriteConfig, verifyTransaction, invalidates, onSuccessfulAction, beforeWriteCheck } =
    config
  const { walletType } = useWalletType()

  const queryClient = useQueryClient()

  const actionHash = useMemo(() => hashAction(action), [action])
  const initialParamsKey = useMemo(
    () => [ACTION_QUERY_PREFIX, 'initial-params', actionId, actionHash] as const,
    [actionId, actionHash],
  )
  const verifyTxKey = useMemo(
    () => [ACTION_QUERY_PREFIX, 'verify-tx', actionId, actionHash] as const,
    [actionId, actionHash],
  )

  const initialParams = useQuery({
    queryKey: initialParamsKey,
    queryFn: getInitialParams ?? (() => ({ canBeSkipped: false })),
    enabled,
    gcTime: 0,
    staleTime: 0,
  })
  const writeConfig = getWriteConfig(initialParams)

  const write = useRegularOrEip5792Write(
    {
      ...writeConfig,
      enabled,
    },
    {
      onTransactionSettled: {
        mounted: (txReceipt) => {
          context.txReceipts.push([action, txReceipt])
          invalidates().map((queryKey) => void queryClient.invalidateQueries({ queryKey }))
          onSuccessfulAction?.(txReceipt)
        },
        unmounted: () => {
          invalidates().map((queryKey) => void queryClient.invalidateQueries({ queryKey }))
        },
      },
      onBeforeWrite: () =>
        runSingleActionPreWriteLifecycle({
          action: {
            contract: writeConfig,
            beforeWriteCheck,
          },
          chainId: context.chainId,
          walletType,
        }),
      mutationKeyPrefix: [WRITE_QUERY_PREFIX, actionId] as const,
    },
  )

  const verifyTransactionData = useQuery({
    queryKey: verifyTxKey,
    queryFn: verifyTransaction ?? (() => ({ success: true })),
    enabled: enabled && write.status.kind === 'success',
    gcTime: 0,
    staleTime: 0,
  })

  const state = mapStatusesToActionState({
    initialParams,
    write,
    verifyTransactionData,
    enabled,
  })

  return {
    action,
    state,
    onAction: write.write,
  }
}

interface MapStatusesToActionStateParams {
  initialParams: InitialParamsQueryResult
  write: UseRegularOrEip5792WriteResult
  verifyTransactionData: VerifyTransactionResult
  enabled: boolean
}

function mapStatusesToActionState({
  initialParams,
  write,
  verifyTransactionData,
  enabled,
}: MapStatusesToActionStateParams): ActionHandlerState {
  const writeStatus = mapWriteResultToActionState(write)

  if (verifyTransactionData.fetchStatus === 'fetching') {
    return { status: 'loading' }
  }

  if (writeStatus.status === 'success' && verifyTransactionData.data?.success === true) {
    return { status: 'success' }
  }

  // if transaction was a success or is verifying enabled flag doesn't affect the status
  if (!enabled) {
    return { status: 'disabled' }
  }

  if (initialParams.fetchStatus === 'fetching') {
    return { status: 'loading' }
  }

  if (initialParams.status === 'error') {
    return { status: 'error', errorKind: 'initial-params', message: initialParams.error.message }
  }

  if (initialParams.data?.canBeSkipped && initialParams.fetchStatus === 'idle') {
    return { status: 'success' }
  }

  if (verifyTransactionData.status === 'error') {
    return { status: 'error', errorKind: 'tx-verify', message: verifyTransactionData.error.message }
  }

  // Transaction result didn't reach it's objective,
  // for instance user went through the approval flow but manually tweaked approval level
  // and it's still too low
  if (write.status.kind === 'success' && verifyTransactionData.data?.success === false) {
    return { status: 'ready' }
  }

  return writeStatus
}

export function actionToConfig(action: Action, context: ActionContext): ActionConfig {
  switch (action.type) {
    case 'resetApproval':
      return createResetApprovalActionConfig(action, context)
    case 'approve':
      return createApproveActionConfig(action, context)
    case 'deposit':
      return createDepositActionConfig(action, context)
    case 'borrow':
      return createBorrowActionConfig(action, context)
    case 'setUseAsCollateral':
      return createSetUseAsCollateralActionConfig(action, context)
    case 'approveDelegation':
      return createApproveDelegationActionConfig(action, context)
    case 'setUserEMode':
      return createSetUserEModeActionConfig(action, context)
    case 'repay':
      return createRepayActionConfig(action, context)
    case 'claimMarketRewards':
      return createClaimMarketRewardsActionConfig(action, context)
    case 'withdraw':
      return createWithdrawActionConfig(action, context)
    case 'withdrawFromSavings':
      return createWithdrawFromSavingsActionConfig(action, context)
    case 'depositToSavings':
      return createDepositToSavingsActionConfig(action, context)
    case 'upgradeSdai':
      return createUpgradeSdaiActionConfig(action, context)
    case 'stake':
      return createStakeActionConfig(action, context)
    case 'unstake':
      return createUnstakeActionConfig(action, context)
    case 'swap':
      return createSwapActionConfig(action, context)
    case 'claimFarmRewards':
      return createClaimFarmRewardsActionConfig(action, context)
    case 'claimPointsRewards':
      return createClaimPointsRewardsActionConfig(action, context)
    case 'claimSparkRewards':
      return createClaimSparkRewardsActionConfig(action, context)
    case 'stakeSpk':
      return createStakeSpkActionConfig(action, context)
    case 'unstakeSpk':
      return createUnstakeSpkActionConfig(action, context)
    case 'finalizeSpkUnstake':
      return createFinalizeSpkUnstakeActionConfig(action, context)
    case 'permit':
      return createEmptyActionConfig()
    case 'claimAirdropRewards':
      return createClaimAirdropRewardsActionConfig(action, context)
    case 'delegate':
      return createDelegateActionConfig(action, context)
    case 'undelegate':
      return createUndelegateActionConfig(action, context)
    case 'depositToWeth':
      return createDepositToWethActionConfig(action, context)
    case 'withdrawFromWeth':
      return createWithdrawFromWethActionConfig(action, context)
    default:
      assertNever(action)
  }
}

function createEmptyActionConfig(): ActionConfig {
  return {
    getWriteConfig: () => ({}) as GetWriteConfigResult,
    invalidates: () => [],
    beforeWriteCheck: () => {},
  }
}
